<template>
  <div class="contract-selector">
    <v-menu
      v-model="menuOpen"
      offset-y
      :close-on-content-click="false"
      max-height="460"
      :min-width="menuWidth"
      :max-width="420"
      @keydown.native="onMenuKeydown"
    >
      <template #activator="{ on: menuOn, attrs: menuAttrs }">
        <v-tooltip bottom :disabled="menuOpen">
          <template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
            <v-btn
              class="bright--text selector-trigger"
              depressed
              small
              v-bind="{ ...menuAttrs, ...tooltipAttrs }"
              v-on="{ ...menuOn, ...tooltipOn }"
              :disabled="!hasContracts"
              :loading="isLoading"
            >
              <!-- 모바일: 아이콘만 -->
              <v-icon small class="hidden-md-and-up">
                mdi-file-document-outline
              </v-icon>

              <!-- 데스크탑: 3개 타입 모두 표시 -->
              <template v-if="hasContracts">
                <span class="d-none d-md-inline-flex align-center">
                  <template v-for="(info, idx) in selectedSummary">
                    <span
                      v-if="idx > 0"
                      :key="'sep-' + idx"
                      class="trigger-sep"
                    >
                      |
                    </span>
                    <span
                      :key="'item-' + idx"
                      class="trigger-item"
                      :class="{
                        'trigger-item--active':
                          info.address === contractAddress,
                      }"
                    >
                      <span
                        class="trigger-type-dot"
                        :style="{ background: typeBadgeColor(info.type) }"
                      ></span>
                      <span class="trigger-symbol">{{ info.symbol }}</span>
                    </span>
                  </template>
                </span>
              </template>
              <span v-else class="d-none d-md-inline">Contract</span>

              <v-icon small class="ml-1 chevron-icon">
                {{ menuOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </v-btn>
          </template>
          <span>{{ tooltipText }}</span>
        </v-tooltip>
      </template>

      <v-card class="contract-dropdown">
        <!-- 빈 상태 -->
        <div v-if="!hasContracts" class="empty-state pa-6 text-center">
          <v-icon large color="grey lighten-1">
            mdi-file-document-remove-outline
          </v-icon>
          <div class="grey--text mt-2 text-body-2">
            {{ $t('contract.noContracts') }}
          </div>
        </div>

        <!-- 컨트랙트 목록 -->
        <v-list v-else dense class="contract-list py-0">
          <template v-for="(group, tokenType, groupIdx) in groupedContracts">
            <v-divider v-if="groupIdx > 0" :key="'div-' + tokenType" />
            <v-subheader :key="'header-' + tokenType" class="group-header">
              <span
                class="group-dot"
                :style="{ background: typeBadgeColor(tokenType) }"
              ></span>
              {{ tokenType.toUpperCase() }}
              <v-chip x-small label class="ml-2 group-count" outlined>
                {{ group.length }}
              </v-chip>
              <v-spacer />
              <!-- 현재 선택 심볼 요약 -->
              <span class="group-selected grey--text text-caption">
                {{ getSelectedSymbol(tokenType) }}
              </span>
            </v-subheader>

            <v-list-item
              v-for="(item, itemIdx) in group"
              :key="item.contractAddress"
              @click="selectContract(item)"
              :class="{
                'selected-item': isSelected(item),
                'focused-item': isFocused(groupIdx, itemIdx),
              }"
              class="contract-item"
              :ref="'item-' + groupIdx + '-' + itemIdx"
            >
              <v-list-item-content>
                <v-list-item-title class="d-flex align-center">
                  <v-icon
                    v-if="isSelected(item)"
                    small
                    color="success"
                    class="mr-2 check-icon"
                  >
                    mdi-check-circle
                  </v-icon>
                  <span v-else class="check-placeholder mr-2"></span>

                  <span class="item-symbol font-weight-bold">
                    {{ item.tokenSymbol }}
                  </span>

                  <v-tooltip bottom open-delay="400">
                    <template #activator="{ on, attrs }">
                      <span
                        class="item-address ml-2"
                        v-bind="attrs"
                        v-on="on"
                        @click.stop="copyAddress(item.contractAddress)"
                      >
                        {{ shortenAddress(item.contractAddress) }}
                        <v-icon x-small class="copy-icon ml-1">
                          mdi-content-copy
                        </v-icon>
                      </span>
                    </template>
                    <span>
                      {{ item.contractAddress }}
                      <br />
                      {{ $t('contract.clickToCopy') }}
                    </span>
                  </v-tooltip>

                  <v-spacer />

                  <v-chip
                    x-small
                    label
                    :color="chainColor(item.chainID)"
                    text-color="white"
                    class="chain-chip"
                  >
                    {{ getChainName(item.chainID) }}
                  </v-chip>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-list>

        <!-- 복사 완료 스낵바 (드롭다운 내부) -->
        <v-fade-transition>
          <div v-if="copied" class="copy-toast">
            <v-icon x-small color="white" class="mr-1">mdi-check</v-icon>
            {{ $t('contract.addressCopied') }}
          </div>
        </v-fade-transition>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
export default {
  name: 'ContractSelector',
  data() {
    return {
      menuOpen: false,
      copied: false,
      copyTimer: null,
      focusGroup: -1,
      focusItem: -1,
    }
  },
  computed: {
    contractList() {
      return this.$store.getters['auth/contractList'] || []
    },
    hasContracts() {
      return this.contractList.length > 0
    },
    isLoading() {
      return this.$store.getters.loading && this.contractList.length === 0
    },
    erc20Address() {
      return this.$store.getters['auth/erc20Address']
    },
    erc721Address() {
      return this.$store.getters['auth/erc721Address']
    },
    erc1155Address() {
      return this.$store.getters['auth/erc1155Address']
    },
    contractAddress() {
      return this.$store.getters['auth/contractAddress']
    },
    groupedContracts() {
      const groups = {}
      const order = ['erc20', 'erc721', 'erc1155']
      order.forEach((type) => {
        const items = this.contractList.filter(
          (item) => item.tokenType === type
        )
        if (items.length > 0) {
          groups[type] = items
        }
      })
      return groups
    },
    groupKeys() {
      return Object.keys(this.groupedContracts)
    },
    flatItems() {
      const items = []
      this.groupKeys.forEach((type, groupIdx) => {
        this.groupedContracts[type].forEach((item, itemIdx) => {
          items.push({ item, groupIdx, itemIdx })
        })
      })
      return items
    },
    selectedSummary() {
      const types = ['erc20', 'erc721', 'erc1155']
      const addressMap = {
        erc20: this.erc20Address,
        erc721: this.erc721Address,
        erc1155: this.erc1155Address,
      }
      const result = []
      types.forEach((type) => {
        const addr = addressMap[type]
        if (!addr) return
        const found = this.contractList.find(
          (c) => c.contractAddress === addr && c.tokenType === type
        )
        if (found) {
          result.push({
            type,
            symbol: found.tokenSymbol,
            address: found.contractAddress,
          })
        }
      })
      return result
    },
    tooltipText() {
      if (!this.hasContracts) return this.$t('contract.noContractList')
      const parts = this.selectedSummary.map(
        (s) => `${s.type.toUpperCase()}: ${s.symbol}`
      )
      return parts.length > 0
        ? parts.join(' / ')
        : this.$t('contract.selectContract')
    },
    menuWidth() {
      return this.$vuetify.breakpoint.xsOnly ? 280 : 360
    },
  },
  watch: {
    menuOpen(val) {
      if (!val) {
        this.focusGroup = -1
        this.focusItem = -1
      }
    },
  },
  methods: {
    selectContract(item) {
      const type = item.tokenType
      if (type === 'erc20') {
        this.$store.dispatch('auth/setErc20Address', item.contractAddress)
      } else if (type === 'erc721') {
        this.$store.dispatch('auth/setErc721Address', item.contractAddress)
      } else if (type === 'erc1155') {
        this.$store.dispatch('auth/setErc1155Address', item.contractAddress)
      }
      this.$store.dispatch('auth/setChainID', item.chainID)
      this.$store.dispatch('auth/setContractAddress', item.contractAddress)
      this.menuOpen = false
    },
    isSelected(item) {
      const type = item.tokenType
      if (type === 'erc20') return item.contractAddress === this.erc20Address
      if (type === 'erc721') return item.contractAddress === this.erc721Address
      if (type === 'erc1155')
        return item.contractAddress === this.erc1155Address
      return false
    },
    isFocused(groupIdx, itemIdx) {
      return this.focusGroup === groupIdx && this.focusItem === itemIdx
    },
    getSelectedSymbol(tokenType) {
      const addressMap = {
        erc20: this.erc20Address,
        erc721: this.erc721Address,
        erc1155: this.erc1155Address,
      }
      const addr = addressMap[tokenType]
      if (!addr) return ''
      const found = this.contractList.find(
        (c) => c.contractAddress === addr && c.tokenType === tokenType
      )
      return found ? found.tokenSymbol : ''
    },
    shortenAddress(addr) {
      if (!addr || addr.length < 12) return addr
      return addr.slice(0, 6) + '\u2026' + addr.slice(-4)
    },
    copyAddress(addr) {
      if (!navigator.clipboard) return
      navigator.clipboard.writeText(addr)
      this.copied = true
      clearTimeout(this.copyTimer)
      this.copyTimer = setTimeout(() => {
        this.copied = false
      }, 1500)
    },
    getChainName(chainID) {
      const chains = { 1: 'ETH', 56: 'BSC', 137: 'MATIC' }
      return chains[chainID] || `Chain ${chainID}`
    },
    chainColor(chainID) {
      const colors = { 1: '#1565C0', 56: '#F9A825', 137: '#7B1FA2' }
      return colors[chainID] || '#757575'
    },
    typeBadgeColor(type) {
      const colors = { erc20: '#43A047', erc721: '#1E88E5', erc1155: '#FB8C00' }
      return colors[type] || '#757575'
    },
    // 키보드 탐색
    onMenuKeydown(e) {
      if (!this.menuOpen) return
      const key = e.key
      if (key === 'ArrowDown' || key === 'ArrowUp') {
        e.preventDefault()
        this.moveFocus(key === 'ArrowDown' ? 1 : -1)
      } else if (key === 'Enter') {
        e.preventDefault()
        this.selectFocused()
      } else if (key === 'Escape') {
        this.menuOpen = false
      }
    },
    moveFocus(direction) {
      const flat = this.flatItems
      if (flat.length === 0) return

      // 현재 flat index 찾기
      let currentFlat = flat.findIndex(
        (f) => f.groupIdx === this.focusGroup && f.itemIdx === this.focusItem
      )
      if (currentFlat === -1) {
        currentFlat = direction > 0 ? -1 : flat.length
      }
      const nextFlat = currentFlat + direction
      if (nextFlat < 0 || nextFlat >= flat.length) return

      this.focusGroup = flat[nextFlat].groupIdx
      this.focusItem = flat[nextFlat].itemIdx

      // 스크롤
      this.$nextTick(() => {
        const ref = this.$refs['item-' + this.focusGroup + '-' + this.focusItem]
        if (ref && ref[0] && ref[0].$el) {
          ref[0].$el.scrollIntoView({ block: 'nearest' })
        }
      })
    },
    selectFocused() {
      if (this.focusGroup < 0 || this.focusItem < 0) return
      const type = this.groupKeys[this.focusGroup]
      if (!type) return
      const group = this.groupedContracts[type]
      if (!group || !group[this.focusItem]) return
      this.selectContract(group[this.focusItem])
    },
  },
  beforeDestroy() {
    clearTimeout(this.copyTimer)
  },
}
</script>

<style scoped>
.contract-selector {
  display: inline-flex;
  align-items: center;
}

/* ── 트리거 버튼 ── */
.selector-trigger {
  background: rgba(255, 255, 255, 0.1) !important;
  border-radius: 6px;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
  font-size: 13px;
  padding: 0 10px !important;
  height: 32px !important;
  transition: background 0.2s;
}
.selector-trigger:hover {
  background: rgba(255, 255, 255, 0.18) !important;
}

.trigger-item {
  display: inline-flex;
  align-items: center;
  opacity: 0.7;
  transition: opacity 0.15s;
}
.trigger-item--active {
  opacity: 1;
}
.trigger-type-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-right: 4px;
}
.trigger-symbol {
  font-size: 12px;
  font-weight: 600;
}
.trigger-sep {
  margin: 0 6px;
  opacity: 0.3;
  font-size: 11px;
}
.chevron-icon {
  opacity: 0.7;
}

/* ── 드롭다운 카드 ── */
.contract-dropdown {
  overflow: hidden;
}
.contract-list {
  max-height: 400px;
  overflow-y: auto;
}

/* ── 그룹 헤더 ── */
.group-header {
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}
.group-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
}
.group-count {
  height: 16px !important;
  font-size: 10px !important;
}
.group-selected {
  font-weight: 600;
  font-size: 11px;
}

/* ── 리스트 아이템 ── */
.contract-item {
  min-height: 40px;
  transition: background 0.15s;
  cursor: pointer;
}

/* Light mode */
.contract-item:hover {
  background: rgba(0, 0, 0, 0.04);
}
.selected-item {
  background: rgba(76, 175, 80, 0.08);
}
.selected-item:hover {
  background: rgba(76, 175, 80, 0.12);
}
.focused-item {
  background: rgba(0, 0, 0, 0.08);
}

/* Dark mode */
.theme--dark .contract-item:hover {
  background: rgba(255, 255, 255, 0.06);
}
.theme--dark .selected-item {
  background: rgba(102, 187, 106, 0.12);
}
.theme--dark .selected-item:hover {
  background: rgba(102, 187, 106, 0.18);
}
.theme--dark .focused-item {
  background: rgba(255, 255, 255, 0.1);
}

/* ── 아이템 내부 ── */
.check-icon {
  flex-shrink: 0;
}
.check-placeholder {
  display: inline-block;
  width: 16px;
  flex-shrink: 0;
}
.item-symbol {
  font-size: 13px;
  min-width: 50px;
}
.item-address {
  font-size: 11px;
  font-family: 'Roboto Mono', monospace;
  opacity: 0.55;
  cursor: pointer;
  border-radius: 3px;
  padding: 1px 4px;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
}
.item-address:hover {
  opacity: 1;
  background: rgba(0, 0, 0, 0.06);
}
.theme--dark .item-address:hover {
  background: rgba(255, 255, 255, 0.08);
}
.copy-icon {
  opacity: 0;
  transition: opacity 0.15s;
}
.item-address:hover .copy-icon {
  opacity: 0.7;
}

.chain-chip {
  height: 18px !important;
  font-size: 10px !important;
  font-weight: 600;
  flex-shrink: 0;
}

/* ── 빈 상태 ── */
.empty-state {
  min-height: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* ── 복사 토스트 ── */
.copy-toast {
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  display: flex;
  align-items: center;
  pointer-events: none;
  z-index: 1;
}

/* ── 반응형 ── */
@media (max-width: 960px) {
  .selector-trigger {
    padding: 0 6px !important;
    min-width: 36px !important;
  }
}
</style>
